import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import ApiClient from '../../../utils/api/ApiClient'

import { RootState } from '../../../app/store'
import {
  readPersistedPrescriber,
  transformPrescriberForService,
  transformPrescriberForUI,
} from '../../PrescriberProfilePage/utils'
import { Prescriber } from '../../../types'
import { PrescriberCreation } from '../../../types/prescriber'
import { STATUS } from '../../../constants'

export interface PrescriberContextState {
  prescriber: Prescriber | null
  workPrescriberProfileId: string | null
  showOnlyMyPrescriptions: boolean
  loading: {
    [K in PrescriberClientMethods]?: string
  }
  status: {
    [K in PrescriberClientMethods]?: STATUS
  }
}

const prescriber = readPersistedPrescriber(localStorage.getItem('prescriber'))

const initialState: PrescriberContextState = {
  prescriber,
  workPrescriberProfileId: null,
  showOnlyMyPrescriptions: true,
  loading: {},
  status: {},
}

export type PrescriberBriefInfo = {
  id: string
  givenNames: string
  familyName: string
}

export type UpdatePrescriberRequest = Prescriber
export type UpdatePrescriberResponse = Prescriber

export type CreatePrescriberRequest = PrescriberCreation
export type CreatePrescriberResponse = PrescriberCreation
export interface HpiiValidateRequest {
  hpii: string
  family_name: string
  clinicId: string
}
export interface HpiiValidateResponse {
  hpii: string
  hpii_status: string
}

export const PrescriberClient = {
  checkHpii: async (request: HpiiValidateRequest) => {
    const query = new URLSearchParams({
      hpiiNumber: request.hpii,
      clinicId: request.clinicId ?? '',
      familyName: request.family_name,
    })

    const response = await ApiClient.get(`/hi-service/prescriber/validate?${query.toString()}`)
    return response.data as HpiiValidateResponse
  },
  checkReferralCode: async (referralCode: string): Promise<PrescriberBriefInfo> => {
    const { data } = await ApiClient.get(`/bff/prescribers/referrer/${referralCode}`)
    return data
  },
  createSimpleProfile: async (payload: {
    email: string
    userId: string
    organizationId: string
    firstName: string
    lastName: string
  }) => {
    const { data } = await ApiClient.post(`/bff/prescribers/simple-profile`, payload)
    return data
  },
  updateActiveStatus: async ({
    prescriberId,
    active,
  }: {
    prescriberId: string
    active: boolean
  }) => {
    const { data } = await ApiClient.patch(`/bff/prescribers/${prescriberId}/active`, { active })
    return data
  },
  GetPrescriber: createAsyncThunk<GetPrescriberResult, void>(
    'prescriber/get',
    async (_, thunkAPI) => {
      const expectedPrescriberId = (thunkAPI.getState() as RootState).prescriber.prescriber?.id

      // A refresh only owns the prescriber it started for. Checked before the request
      // and again once it resolves, because logging out, changing profile or a newer
      // refresh all invalidate this response - and writing it would resurrect the
      // previous prescriber in state and in localStorage.
      const stillOwnsPrescriber = () => {
        const state = thunkAPI.getState() as RootState
        return (
          !!expectedPrescriberId &&
          state.prescriber.loading.GetPrescriber === thunkAPI.requestId &&
          state.prescriber.prescriber?.id === expectedPrescriberId
        )
      }

      if (!stillOwnsPrescriber()) return { status: 'superseded' }

      try {
        const response = await ApiClient.get(`/prescribers/current`)

        if (!stillOwnsPrescriber()) return { status: 'superseded' }

        return { status: 'loaded', prescriber: transformPrescriberForUI(response.data) }
      } catch (error) {
        console.error(error)
        // Distinguish failure from supersession: the reducer clears the loading flag
        // but keeps the last known prescriber, so a transient failure cannot sign the
        // prescriber out of their own session.
        return { status: 'failed' }
      }
    }
  ),
  CreatePrescriber: createAsyncThunk(
    'prescriber/create',
    async (request: CreatePrescriberRequest, thunkAPI) => {
      const state = thunkAPI.getState() as RootState
      if (state.prescriber.loading.CreatePrescriber !== thunkAPI.requestId) return
      try {
        const prescriber = transformPrescriberForService(request)
        const response = await ApiClient.post(`/bff/prescribers`, prescriber)

        return {
          prescriber: transformPrescriberForUI(response.data),
        }
      } catch (error) {
        console.error(error)
        throw error
      }
    }
  ),
  UpdatePrescriber: createAsyncThunk(
    'prescriber/update',
    async (request: UpdatePrescriberRequest, thunkAPI) => {
      const state = thunkAPI.getState() as RootState
      if (state.prescriber.loading.UpdatePrescriber !== thunkAPI.requestId) return

      try {
        if (!state.prescriber.prescriber) throw new Error('No Prescriber Logged In')

        const updatedPrescriber = transformPrescriberForService(request)
        const response = await ApiClient.patch(`/prescribers/current`, updatedPrescriber)

        setTimeout(() => {
          thunkAPI.dispatch(PrescriberClient.GetPrescriber())
        }, 300)

        return response.data as UpdatePrescriberResponse
      } catch (error) {
        console.error(error)
        throw error
      }
    }
  ),
  AcceptAgreements: createAsyncThunk(
    'prescriber/accept_agreement',
    async (request: any, { getState, requestId }) => {
      const state = getState() as RootState
      if (state.prescriber.loading.AcceptAgreements !== requestId) return

      try {
        const response = await ApiClient.put(`/bff/prescribers/agreements`, request)
        return { prescriber: response.data } as GetPrescriberResponse
      } catch (error) {
        console.error(error)
      }
    }
  ),
}
type PrescriberClientMethods = keyof typeof PrescriberClient
export interface GetPrescriberResponse {
  prescriber: Prescriber
}

/**
 * `prescriber/get` resolves instead of rejecting, so the fulfilled reducer can tell
 * a genuine refresh apart from a superseded request and a failed one.
 */
export type GetPrescriberResult =
  | { status: 'loaded'; prescriber: Prescriber | null }
  | { status: 'superseded' }
  | { status: 'failed' }

export const PrescriberContextSlice = createSlice({
  name: 'productSearch',
  initialState,
  reducers: {
    setPrescriber: (state, { payload: newPrescriber }: PayloadAction<Prescriber | null>) => {
      state.prescriber = newPrescriber
      // The profile changed, so any refresh already in flight is orphaned. Release the
      // flag here, otherwise the discarded response keeps holding it and every later
      // refresh bails out at the ownership check.
      state.loading.GetPrescriber = undefined
      if (newPrescriber) {
        localStorage.setItem('prescriber', JSON.stringify(newPrescriber))
      } else {
        localStorage.removeItem('prescriber')
      }
    },
    setShowOnlyMyPrescriptions: (state, { payload }: PayloadAction<boolean>) => {
      state.showOnlyMyPrescriptions = payload
    },
    setWorkPrescriberProfileId: (state, { payload }: PayloadAction<string>) => {
      state.workPrescriberProfileId = payload
    },
    clearWorkPrescriberProfileId: (state) => {
      state.workPrescriberProfileId = null
    },
  },
  extraReducers(builder) {
    builder
      // Get Prescriber
      .addCase(PrescriberClient.GetPrescriber.pending, (state, { meta: { requestId } }) => {
        if (!state.loading.GetPrescriber) {
          state.loading.GetPrescriber = requestId
        }
      })
      .addCase(PrescriberClient.GetPrescriber.fulfilled, (state, { payload, meta }) => {
        // Ownership is decided where the write happens, not only before the request: a
        // response that no longer holds the loading flag was superseded by a newer
        // refresh, or orphaned by a logout or profile switch, and writes nothing at all.
        if (state.loading.GetPrescriber !== meta.requestId) return

        // Still ours, so the request is finished and must release the flag it claimed in
        // `pending`. One that claimed it and then found nothing to refresh (no prescriber)
        // would otherwise hold it for good and shut every later refresh out of the
        // ownership check - the case that used to report `failed` and release it.
        state.loading.GetPrescriber = undefined

        // Superseded and failed refreshes write nothing. A failure keeps the last known
        // prescriber: writing null here used to wipe a live prescriber, and
        // JSON.stringify(undefined) persisted the literal string "undefined" that every
        // reader then had to tolerate.
        if (payload.status !== 'loaded') return

        state.prescriber = payload.prescriber

        // A response with no usable id normalises to null, and the key then goes away
        // rather than holding the string `"null"` - the writer contract in one place.
        if (payload.prescriber) {
          localStorage.setItem('prescriber', JSON.stringify(payload.prescriber))
        } else {
          localStorage.removeItem('prescriber')
        }
      })
      // Update Prescribers
      .addCase(PrescriberClient.UpdatePrescriber.pending, (state, { meta: { requestId } }) => {
        state.loading.UpdatePrescriber = requestId
      })
      .addCase(PrescriberClient.UpdatePrescriber.fulfilled, (state) => {
        state.loading.UpdatePrescriber = undefined
      })
      .addCase(PrescriberClient.UpdatePrescriber.rejected, (state) => {
        state.loading.UpdatePrescriber = undefined
      })
      //Create Prescriber
      .addCase(PrescriberClient.CreatePrescriber.pending, (state, { meta: { requestId } }) => {
        state.loading.CreatePrescriber = requestId
        state.status = {
          ...state?.status,
          CreatePrescriber: STATUS.PENDING,
        }
      })
      .addCase(PrescriberClient.CreatePrescriber.fulfilled, (state) => {
        state.loading.CreatePrescriber = undefined
        state.status = {
          ...state?.status,
          CreatePrescriber: STATUS.SUCCESS,
        }
      })
      .addCase(PrescriberClient.CreatePrescriber.rejected, (state) => {
        state.loading.CreatePrescriber = undefined
        state.status = {
          ...state?.status,
          CreatePrescriber: STATUS.FAILURE,
        }
      })
      .addCase(PrescriberClient.AcceptAgreements.pending, (state, { meta: { requestId } }) => {
        state.loading.AcceptAgreements = requestId
      })
      .addCase(PrescriberClient.AcceptAgreements.fulfilled, (state, { payload }) => {
        if (!state.prescriber) {
          console.error('Prescriber can not be null') // TODO: Logout user and report error?
          return
        }
        state.loading.AcceptAgreements = undefined
        state.prescriber.mimsEula = payload?.prescriber?.mimsEula || null
        state.prescriber.rxpadEula = payload?.prescriber?.rxpadEula || null
        localStorage.setItem('prescriber', JSON.stringify(state.prescriber))
      })
      .addCase(PrescriberClient.AcceptAgreements.rejected, (state) => {
        state.loading.AcceptAgreements = undefined
      })
  },
})

export const {
  setPrescriber,
  setShowOnlyMyPrescriptions,
  setWorkPrescriberProfileId,
  clearWorkPrescriberProfileId,
} = PrescriberContextSlice.actions

export default PrescriberContextSlice.reducer
